import { useEffect, useRef } from 'react'
import { useLocation } from 'react-router-dom'

import { useLocale } from '../contexts/LocaleContext'
import { resolveLocale, setStoredLocale } from '../i18n/detect-locale'
import { getUserProfile } from '../services/api/profile'
import { getAccessToken } from '../services/authentication'
import { isMobileApp } from '../utils/webviewBridge'

/**
 * Headless hook that syncs the Directory-stored locale (users.language) into the
 * local UI once per authenticated session.
 *
 * Resolution priority after this sync (TRAVEL-4158):
 *   1. Directory users.language  ← this hook writes to localStorage + LocaleContext
 *   2. localStorage cache        ← picked up by getLocale() on subsequent boots
 *   3. navigator.languages       ← browser fallback when no preference is stored
 *
 * The access token is read from a cookie, which may still be empty at first render
 * (e.g. a session that authenticates client-side via exchangeToken before landing on
 * an authenticated route). To avoid missing that late token, the sync is retried on
 * every navigation until it finds a token, then applied exactly once per session
 * (guarded by hasSyncedRef).
 *
 * In the native app webview the priority is inverted: the device/app locale
 * (navigator.language) is the source of truth, so this sync is skipped entirely and
 * the Directory account language never overrides the app's language. This mirrors the
 * isMobileApp() gate applied in the other Travel fronts (okarito-front syncFromDirectory,
 * employee AppProvider).
 *
 * The sync is fire-and-forget: failures are silently swallowed so a network error
 * never breaks the UI. On failure hasSyncedRef stays false, so the next navigation
 * retries — the session is not permanently blocked by a transient error.
 * Gated locales (DE/ES/IT while TRAVEL-4052 is active) are ignored via resolveLocale —
 * a Directory "de-DE" preference will not force the app into an unreleased locale.
 *
 * Must be called inside LocaleProvider and a Router.
 */
export const useDirectoryLocaleSync = (): void => {
  const { setLocale } = useLocale()
  const { pathname } = useLocation()
  const hasSyncedRef = useRef(false)
  const syncingRef = useRef(false)

  useEffect(() => {
    // Native app webview: keep the device/app locale (navigator.language) authoritative.
    if (isMobileApp()) return
    // Already applied this session: the Directory preference is a one-shot.
    // In-flight guard: skip concurrent fetches triggered by rapid navigation.
    if (hasSyncedRef.current || syncingRef.current) return

    const sync = async (): Promise<void> => {
      const accessToken = getAccessToken()
      // No token yet: retry on the next navigation once auth has resolved.
      if (!accessToken) return

      syncingRef.current = true
      try {
        const profile = await getUserProfile(accessToken)
        const resolved = resolveLocale(profile.locale)
        if (resolved) {
          setStoredLocale(resolved)
          setLocale(resolved)
        }
        hasSyncedRef.current = true
      } catch {
        // Non-blocking: keep the cached/browser locale if the profile fetch fails
        // hasSyncedRef stays false so the next navigation will retry
      } finally {
        syncingRef.current = false
      }
    }

    void sync()
  }, [pathname, setLocale]) // retry on each navigation; setLocale is stable (useState setter)
}
